import React, { FC, useCallback, useMemo } from 'react'
import classNames from 'classnames'
import LazyHydrate from 'react-lazy-hydration'
import { AsH1, TitleLG } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { LandscapeRowCollection } from '@/molecules/LandscapeRowCollection'
import { PlaceholderMediaCard } from '@/molecules/MediaCard'
import { WatchlistCard } from '@/organisms/WatchlistCollection/WatchlistCard'
import { DiscoverItemInteraction, useCollectionImpressionTracking } from '@/services/Analytics/ContentInteractions'
import { createWebClient } from '@/services/ApolloClient'
import { LinkViewModel } from '@/services/RenderService'
import { useUser } from '@/services/UserService'
import { useGetDefaultWatchlist, WatchlistItem } from '@/services/WatchlistService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { AnalyticsEvent } from '@/utils/analytics'
import { TranslateFunction, useTranslate } from '@/utils/translate/translate-client'

export interface WatchlistCollectionProps {
  containerPosition?: number
  titleClassName?: string
}

export const WatchlistCollection: FC<WatchlistCollectionProps> = ({ containerPosition = 0, titleClassName }) => {
  const { t } = useTranslate('watch')
  const { locale } = useLocale()
  const { userId } = useUser()
  // TODO: when web implements user profiles, use that profile id here instead.
  const viewerProfileId = userId ?? undefined

  const { hoverStartTimeRef, impressionTimeoutRef, hasTrackedImpression } = useCollectionImpressionTracking()

  const client = useMemo(() => {
    return createWebClient({ locale, viewerProfileId })
  }, [locale, viewerProfileId])

  const { defaultWatchlist, loading } = useGetDefaultWatchlist({ client, viewerProfileId })

  const createInteraction = useCallback(
    (watchlistItem: WatchlistItem, itemIndex: number) => {
      const contentId = watchlistItem?.id ?? ''
      const typename = watchlistItem?.__typename
      const imageUrl = watchlistItem?.imageLandscape?.cloudinaryPath

      if (!contentId || !typename || !imageUrl) return null

      return new DiscoverItemInteraction({
        id: contentId,
        __typename: typename as
          | 'ContentEpisode'
          | 'ContentLivestream'
          | 'ContentSeries'
          | 'ContentMovie'
          | 'ContentSpecial',
        attribution: (watchlistItem as unknown as { attribution: unknown })?.attribution,
      })
        .withSurface('discover')
        .withPlacement('watchlist', containerPosition, itemIndex, 'horizontal_swimlane')
        .withImageArtwork(getCloudinaryImageUrl({ path: imageUrl }), 'landscape_display')
    },
    [containerPosition],
  )

  if (!loading && defaultWatchlist.length === 0) {
    return null
  }

  return (
    <div data-testid="your-watchlist-collection" className="relative">
      <header>
        <TitleLG
          weight="bold"
          as={AsH1}
          data-testid="your-watchlist-header"
          className={classNames('mb-3 md:mb-4', titleClassName)}
        >
          {t('yourWatchlist', 'Your Watchlist')}
        </TitleLG>
      </header>
      {/* We get the User's Watchlist client side so handle hydration here */}
      <LazyHydrate whenVisible>
        <LandscapeRowCollection>
          {loading
            ? Array(5)
                .fill(0)
                .map((v, i) => (
                  <PlaceholderMediaCard
                    className="mr-4"
                    borderRadiusClassName="rounded-lg"
                    hideTextPlaceholders
                    key={i}
                  />
                ))
            : defaultWatchlist.map((watchlistItem, itemIndex) => {
                const linkModel: LinkViewModel | null = buildLinkModel(watchlistItem)

                if (!linkModel || !watchlistItem?.project?.slug) return null

                const title = formatTitle(watchlistItem, t)

                return (
                  <WatchlistCard
                    key={watchlistItem?.id}
                    watchlistItem={watchlistItem}
                    linkModel={linkModel}
                    itemIndex={itemIndex}
                    title={title}
                    hoverStartTimeRef={hoverStartTimeRef}
                    impressionTimeoutRef={impressionTimeoutRef}
                    hasTrackedImpression={hasTrackedImpression}
                    createInteraction={createInteraction}
                  />
                )
              })}
        </LandscapeRowCollection>
      </LazyHydrate>
    </div>
  )
}

function buildLinkModel(watchlistItem: WatchlistItem): LinkViewModel | null {
  if (
    !watchlistItem?.id ||
    !watchlistItem?.project?.slug ||
    !watchlistItem?.name ||
    !watchlistItem?.imageLandscape?.cloudinaryPath
  )
    return null

  return {
    id: watchlistItem.id,
    linkUrl: `${paths.watch.index}/${watchlistItem.project.slug}`,
    imageUrl: getCloudinaryImageUrl({
      path: watchlistItem?.imageLandscape?.cloudinaryPath ?? '',
    }),
    logoUrl: '',
    label: watchlistItem.name ?? undefined,
    name: watchlistItem.name ?? undefined,
    projectSlug: watchlistItem.project.slug,
    track: {
      eventName: 'Clicked Watchlist Item' as AnalyticsEvent,
      payload: {
        program: watchlistItem.id,
        title: watchlistItem?.name,
        subtitle: watchlistItem?.name,
      },
    },
  }
}

function formatTitle(watchlistItem: WatchlistItem, t: TranslateFunction) {
  if (!watchlistItem) return undefined

  if (watchlistItem.name?.toLowerCase() === 'minisode') {
    return t('minisode', 'Minisode')
  } else if (watchlistItem?.__typename === 'ContentSeries') {
    return t('seriesDescription', 'series')
  } else if (watchlistItem?.__typename === 'ContentSpecial') {
    return t('special', 'Special')
  } else if (watchlistItem?.__typename === 'ContentMovie') {
    return t('film', 'Film')
  }
}
